import { AdminAccountFormValues } from '@/types/pages/admin/account';
import { Box, Collapse } from '@mui/material';
import { Check, Close, Lock } from '@mui/icons-material';
import { editAccountAPI } from '@/api/admin/rbac';
import { EditAccountRequest } from '@/types/pages/admin/apiRBAC';
import { getEnvBool } from '@/utils/env';
import { useAppContext } from '@/provider/contexts';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseDialog from '@/components/ui/BaseDialog';
import localforage from 'localforage';
import PasswordField from '@/components/form/PasswordField';

interface AdminChangePasswordProps {
    open: boolean;
    close: () => void;
    sForm: AdminAccountFormValues;
    id: string | undefined;
    setSForm: (data: Partial<AdminAccountFormValues>) => void;
    setPageLoading: (prev: boolean) => void;
}

enum CHANGE_PASSWORD_TYPE {
    NEW = 'NEW',
    EDIT = 'EDIT',
}

export const AdminChangePasswordDialog = ({
    open,
    sForm,
    id,
    close,
    setSForm,
    setPageLoading,
}: AdminChangePasswordProps) => {
    const { t } = useTranslation('common');
    const enforceIsoControls = getEnvBool('VITE_ENFORCE_ISO27002_SECURITY_CONTROLS', false);
    const useStrictPasswordValidation = enforceIsoControls;
    const { setNotification } = useAppContext();
    const changeType = id ? CHANGE_PASSWORD_TYPE.EDIT : CHANGE_PASSWORD_TYPE.NEW;

    const [newPassword, setNewPassword] = useState<string>('');
    const [pwdValidation, setPwdValidation] = useState({
        lower: false,
        upper: false,
        number: false,
        special: false,
        length: false,
        maxLength: true,
        noUsername: false,
    });

    const [, setVisible] = useState(false);
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        const pw = newPassword;
        const username = sForm.username.toLowerCase();
        setPwdValidation({
            lower: useStrictPasswordValidation ? /[a-z]/.test(pw) : true,
            upper: useStrictPasswordValidation ? /[A-Z]/.test(pw) : true,
            number: useStrictPasswordValidation ? /\d/.test(pw) : true,
            special: useStrictPasswordValidation ? /[!@#$%^&*()\-_=+\\[\]{}\\|;:'",.<>/?`~]/.test(pw) : true,
            length: useStrictPasswordValidation ? pw.length >= 8 : pw.length > 0,
            maxLength: pw.length <= 128,
            noUsername: useStrictPasswordValidation ? (username ? !pw.toLowerCase().includes(username) : true) : true,
        });
    }, [newPassword, sForm.username]);

    const clear = () => {
        close();
        setTimeout(() => {
            setNewPassword('');
            setFocused(false);
            setVisible(false);
        });
    };

    const handleOnConfirm = () => {
        if (!newPassword) return;
        close();
        if (changeType === CHANGE_PASSWORD_TYPE.EDIT) {
            setPageLoading(true);
            resetPassword();
        }
        if (changeType === CHANGE_PASSWORD_TYPE.NEW) {
            setSForm({ password: newPassword });
        }
    };

    const resetPassword = async () => {
        try {
            const corporationId = (await localforage.getItem('corporation_id')) as number;
            const editData = { corporate_id: corporationId, new_password: newPassword } as EditAccountRequest;
            const {
                data: { isSuccess, message },
            } = await editAccountAPI(Number(id), editData);
            clear();
            setPageLoading(false);
            if (isSuccess) {
                setNotification({
                    message: t(message),
                    severity: 'success',
                });
                return;
            }
            setNotification({
                message: t(message),
                severity: 'error',
            });
        } catch (error) {
            console.error('An unexpected error occurred:', error);
            clear();
            setPageLoading(false);
            setNotification({
                message: t('An unexpected error occurred'),
                severity: 'error',
            });
        }
    };
    return (
        <BaseDialog
            open={open}
            onClose={clear}
            onConfirm={handleOnConfirm}
            dialogTitle={id ? t('Change Password') : t('New Password')}
            confirmButtonText={t('Save')}
            cancelButtonText={t('Cancel')}
            disableConfirm={!Object.values(pwdValidation).every((v) => v)}
            showSpinnerOnDisable={false}
            dialogContentProps={{
                sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 2,
                    pt: 2,
                    pb: 1,
                    overflow: 'visible',
                },
            }}
            TransitionProps={{
                onExited: () => {
                    if (changeType === CHANGE_PASSWORD_TYPE.NEW) setNewPassword(sForm.password);
                },
            }}
        >
            <PasswordField
                placeholder={t('New Password')}
                autoComplete="new-password"
                startIcon={<Lock />}
                fullWidth
                variant="outlined"
                margin="normal"
                value={newPassword}
                inputProps={{ maxLength: 128 }}
                onChange={(e) => setNewPassword(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                ariaLabelShow={t('Show password')}
                ariaLabelHide={t('Hide password')}
            />

            {useStrictPasswordValidation && (
                <Collapse in={focused || !!newPassword} timeout="auto" unmountOnExit>
                    <Box sx={{ px: 2, pt: 1 }}>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 14 }}>
                            {(
                                [
                                    useStrictPasswordValidation && ['lower', t('One lowercase character')],
                                    useStrictPasswordValidation && ['upper', t('One uppercase character')],
                                    useStrictPasswordValidation && ['number', t('One number')],
                                    useStrictPasswordValidation && ['special', t('One special character')],
                                    useStrictPasswordValidation && ['length', t('8 characters minimum')],
                                    useStrictPasswordValidation && ['maxLength', t('128 characters maximum')],
                                    useStrictPasswordValidation && ['noUsername', t('Must not contain username')],
                                ].filter(Boolean) as [keyof typeof pwdValidation, string][]
                            ).map(([key, label]) => (
                                <li key={key} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                                    {pwdValidation[key] ? (
                                        <Check fontSize="small" color="success" />
                                    ) : (
                                        <Close fontSize="small" color="disabled" />
                                    )}
                                    <span style={{ marginLeft: 8, color: pwdValidation[key] ? 'inherit' : '#999' }}>
                                        {label}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </Box>
                </Collapse>
            )}
        </BaseDialog>
    );
};
